import type { HelpEntry } from './types'

/** Split a path into non-empty segments ("/a/b/" → ["a","b"]). */
function segs(path: string): string[] {
  return path.split('?')[0].split('#')[0].split('/').filter(Boolean)
}

/** Does a route pattern ("/journeys/$journeyId") match a concrete pathname? */
export function matchRoute(pattern: string, pathname: string): boolean {
  const p = segs(pattern)
  const s = segs(pathname)
  if (p.length !== s.length) return false
  return p.every((x, i) => x.startsWith('$') || x === decodeURIComponent(s[i]))
}

/** Specificity: literal segments beat parameters (so /journeys/review wins over /journeys/$journeyId). */
function score(pattern: string): number {
  return segs(pattern).reduce((n, x) => n + (x.startsWith('$') ? 1 : 3), 0)
}

export interface Resolved {
  entry: HelpEntry
  /** true when no entry matched the route itself and a section-level entry was used. */
  fallback: boolean
}

/**
 * Find the help entry for a pathname: an exact route match (most specific wins),
 * else the entry of the longest literal prefix (section level, e.g. /finance/x → /finance),
 * else the overview entry (route "/").
 */
export function resolveHelp(pathname: string, entries: HelpEntry[]): Resolved | undefined {
  const exact = entries.filter((e) => matchRoute(e.route, pathname)).sort((a, b) => score(b.route) - score(a.route))
  if (exact[0]) return { entry: exact[0], fallback: false }
  const s = segs(pathname)
  for (let n = s.length - 1; n >= 1; n--) {
    const prefix = '/' + s.slice(0, n).join('/')
    const hit = entries.find((e) => matchRoute(e.route, prefix))
    if (hit) return { entry: hit, fallback: true }
  }
  const home = entries.find((e) => e.route === '/')
  return home ? { entry: home, fallback: true } : undefined
}

export function entryByKey(key: string, entries: HelpEntry[]): HelpEntry | undefined {
  return entries.find((e) => e.key === key)
}

export function entryByRoute(route: string, entries: HelpEntry[]): HelpEntry | undefined {
  return entries.find((e) => e.route === route)
}
